import {announce} from '@github-ui/aria-live'
import {useCreateScribeArtifact} from '@github-ui/scribe-artifacts'
import {PencilIcon} from '@primer/octicons-react'
import {Banner, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {
  type ChangeEvent,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react'

import styles from './CreateArtifactDialog.module.css'

const CREATE_ARTIFACT_DIALOG_TITLE = 'Create new artifact'
const CREATE_ARTIFACT_BODY_PLACEHOLDER = 'Write markdown...'
const MAX_DERIVED_ARTIFACT_NAME_LENGTH = 100

export function deriveArtifactName(body: string): string {
  return (
    body
      .trim()
      .split(/\r?\n/)
      .find(line => line.trim())
      ?.trim()
      .slice(0, MAX_DERIVED_ARTIFACT_NAME_LENGTH) ?? ''
  )
}

type CreateArtifactDialogProps = {
  resourceId: number
  onClose: () => void
  onCreated: () => void
  returnFocusRef: RefObject<HTMLElement | null>
}

export function CreateArtifactDialog({resourceId, onClose, onCreated, returnFocusRef}: CreateArtifactDialogProps) {
  const formId = useId()
  const markdownInputRef = useRef<HTMLTextAreaElement>(null)
  const titleBeforeEditRef = useRef<string | null>(null)
  // Set when edit mode ends via keyboard (Enter/Escape) so focus returns to the
  // title button as it remounts. Blur is user-driven and must not pull focus back.
  const returnFocusToTitleButtonRef = useRef(false)
  const [body, setBody] = useState('')
  // null means "follow the auto-generated title"; a string means the user has
  // taken over the title manually.
  const [manualTitle, setManualTitle] = useState<string | null>(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [createErrorMessage, setCreateErrorMessage] = useState('')
  const {mutate: createArtifact, isPending} = useCreateScribeArtifact({resourceType: 'issue', resourceId})

  const derivedTitle = deriveArtifactName(body)
  const effectiveTitle = manualTitle ?? derivedTitle
  const bodyIsEmpty = body.trim().length === 0

  const beginEditingTitle = useCallback(() => {
    // Snapshot the current title so Escape can revert to it.
    titleBeforeEditRef.current = manualTitle
    setIsEditingTitle(true)
  }, [manualTitle])

  const commitTitle = useCallback(() => {
    setIsEditingTitle(false)
    setManualTitle(prev => (prev !== null && prev.trim() === '' ? null : prev))
  }, [])

  const cancelEditingTitle = useCallback(() => {
    setManualTitle(titleBeforeEditRef.current)
    setIsEditingTitle(false)
  }, [])

  // Callback ref on the title button. After a keyboard commit/cancel the <input>
  // unmounts in favor of this <button>; move focus to it as it remounts so
  // keyboard users keep their place instead of being dropped on document.body
  // and bounced by the dialog's focus trap. Guarded by the flag so a user-driven
  // blur does not pull focus back.
  const titleButtonRef = useCallback((node: HTMLButtonElement | null) => {
    if (node && returnFocusToTitleButtonRef.current) {
      returnFocusToTitleButtonRef.current = false
      node.focus()
    }
  }, [])

  const handleTitleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') {
        event.preventDefault()
        returnFocusToTitleButtonRef.current = true
        commitTitle()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (event.key === 'Escape') {
        // Keep Escape from bubbling up and closing the whole dialog, and revert
        // to the pre-edit title rather than keeping the live keystrokes.
        event.stopPropagation()
        returnFocusToTitleButtonRef.current = true
        cancelEditingTitle()
      }
    },
    [commitTitle, cancelEditingTitle],
  )

  const handleTitleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setManualTitle(event.currentTarget.value)
  }, [])

  const handleBodyChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setBody(event.currentTarget.value)
    setCreateErrorMessage('')
  }, [])

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      if (bodyIsEmpty) return
      createArtifact(
        {
          name: effectiveTitle.trim() || CREATE_ARTIFACT_DIALOG_TITLE,
          body,
          type: 'generic',
        },
        {
          onSuccess: () => {
            onCreated()
            onClose()
          },
          onError: () => {
            const message = 'Artifact could not be created. Try again.'
            setCreateErrorMessage(message)
            announce(message, {assertive: true})
          },
        },
      )
    },
    [bodyIsEmpty, effectiveTitle, body, createArtifact, onCreated, onClose],
  )

  const titleNode = isEditingTitle ? (
    <input
      className={styles.TitleInput}
      aria-label="Artifact title"
      autoFocus
      value={effectiveTitle}
      placeholder={CREATE_ARTIFACT_DIALOG_TITLE}
      disabled={isPending}
      onChange={handleTitleChange}
      onFocus={event => {
        // Defer the select() layout read out of the focus handler so selecting
        // the existing title text doesn't block the next paint (INP).
        const input = event.currentTarget
        requestAnimationFrame(() => input.select())
      }}
      onBlur={commitTitle}
      onKeyDown={handleTitleKeyDown}
    />
  ) : (
    <button type="button" ref={titleButtonRef} className={styles.TitleButton} onClick={beginEditingTitle}>
      <span className={styles.TitleText}>{effectiveTitle || CREATE_ARTIFACT_DIALOG_TITLE}</span>
      <PencilIcon size={14} className={styles.TitlePencil} />
    </button>
  )

  return (
    <Dialog
      title={titleNode}
      className={clsx(styles.CreateDialog, createErrorMessage && styles.CreateDialogHasBanner)}
      width="xlarge"
      height="large"
      onClose={onClose}
      initialFocusRef={markdownInputRef}
      returnFocusRef={returnFocusRef}
      renderBody={({children}) => <Dialog.Body className={styles.DialogBody}>{children}</Dialog.Body>}
      footerButtons={[
        {content: 'Cancel', onClick: onClose, buttonType: 'normal', disabled: isPending},
        {
          content: 'Create artifact',
          buttonType: 'primary',
          type: 'submit',
          form: formId,
          disabled: bodyIsEmpty || isPending,
          loading: isPending,
          loadingAnnouncement: 'Creating artifact',
        },
      ]}
    >
      <form id={formId} onSubmit={handleSubmit} className={styles.Form}>
        {createErrorMessage && (
          <Banner title="Unable to create artifact" variant="critical" description={createErrorMessage} />
        )}
        <Textarea
          ref={markdownInputRef}
          value={body}
          aria-label="Markdown"
          placeholder={CREATE_ARTIFACT_BODY_PLACEHOLDER}
          block
          resize="none"
          disabled={isPending}
          className={styles.Textarea}
          onChange={handleBodyChange}
        />
      </form>
    </Dialog>
  )
}
